import {
  FileFormat, ReportDocument, ResultSnapshot, TableData,
} from '../../../../agent/runtime/files';
import type { ExperienceCandidate } from '../../../../agent/runtime/experience';
import type { TaskSpec } from '../../../../agent/runtime/task';
import type { UploadedFile } from '../../../../agent/runtime/upload';
import type { QueuedJob } from '../../../../agent/runtime/queued';

export type AgentProgress =
  /** 무엇을 하라는 말인지 읽는 중. */
  | { kind: 'understanding' }
  /** 할 일을 짜는 중. `screen` 은 그동안 미리 열어 둔 화면이다. */
  | { kind: 'planning'; screen?: string }
  /** 그 화면에서 쓰거나 누르는 중. */
  | { kind: 'preparing'; screen: string }
  | { kind: 'retrieving'; label: string; done: number; total: number }
  | { kind: 'computing' }
  /** 계산이 업무 규칙대로 셌는지 확인하는 중. */
  | { kind: 'checking' }
  /** 직전 계산이 받아들여지지 않아 다시 계산하는 중. `reason` 은 사람 말이다. */
  | { kind: 'retrying'; attempt: number; reason: string }
  | { kind: 'answering'; text: string }
  | {
    kind: 'done'; text: string; table?: TableData; routes?: string[]; experience?: ExperienceCandidate;
    formats?: FileFormat[]; result?: ResultSnapshot; report?: ReportDocument;
    task?: TaskSpec;
    /** 서버에 맡겨 나중에 끝나는 일. */
    queued?: QueuedJob[];
    /** 답 아래에 내미는 선택 단추. 누르면 그 글이 다음 질문이 된다. */
    options?: string[];
  }
  | { kind: 'ask'; question: string; options?: string[]; task?: TaskSpec }
  | { kind: 'error'; message: string };

/** 지나간 진행 단계 — 패널이 한 줄씩 쌓아 보인다. 저장하지 않는다. */
export type AgentStepLine = { key: string; text: string; past: string; at: number; ms?: number };

/** 끝난 단계를 짧게 — `요청 파악`, `할 일 정하기`. */
function pastText(progress: AgentProgress): string {
  switch (progress.kind) {
    case 'understanding': return '요청 파악';
    case 'planning': return '할 일 정하기';
    case 'preparing': return `${progress.screen} 화면 작업`;
    case 'retrieving': return `${progress.label} 조회`;
    case 'computing': return '계산';
    case 'checking': return '계산 확인';
    case 'retrying': return progress.reason ? `다시 계산 — ${progress.reason}` : '다시 계산';
    case 'answering': return '답 쓰기';
    default: return '';
  }
}

/** 진행 단계를 사람 말로. 빈 문자열이면 줄을 안 보인다. */
export function progressText(progress: AgentProgress): string {
  switch (progress.kind) {
    case 'understanding': return '무엇을 원하시는지 파악하는 중…';
    case 'planning': return progress.screen
      ? `할 일을 정하는 중… (${progress.screen} 화면을 미리 여는 중)`
      : '어느 화면을 볼지 정하는 중…';
    case 'preparing': return `${progress.screen} 화면에서 작업하는 중…`;
    case 'retrieving': return `${progress.label} 조회 중 (${progress.done}/${progress.total})…`;
    case 'computing': return '계산하는 중…';
    case 'checking': return '계산이 맞는지 확인하는 중…';
    case 'retrying': return `다시 계산하는 중(${progress.attempt}번째)${progress.reason ? ` — ${progress.reason}` : ''}…`;
    case 'answering': return '답을 쓰는 중…';
    case 'error': return progress.message;
    default: return '';
  }
}

/**
 * **단계가 바뀌면 앞 단계를 닫고 걸린 시간을 적는다.** 조회는 같은 표의 건수만 바뀌므로 표 이름으로 한 단계다.
 * 「느리다」가 어느 단계에서 나는지 사용자도 우리도 보게 한다(2026-10-14).
 */
export function nextSteps(steps: AgentStepLine[], progress: AgentProgress, now = Date.now()): AgentStepLine[] {
  const last = steps[steps.length - 1];
  const close = (list: AgentStepLine[]): AgentStepLine[] => (last && last.ms === undefined
    ? [...list.slice(0, -1), { ...last, ms: now - last.at }]
    : list);
  if (progress.kind === 'done' || progress.kind === 'ask' || progress.kind === 'error') return close(steps);
  let key: string = progress.kind;
  if (progress.kind === 'retrieving') key = `retrieving:${progress.label}`;
  if (progress.kind === 'preparing') key = `preparing:${progress.screen}`;
  // 다시 계산은 번째마다 한 줄이다 — 왜 다시 했는지가 쌓여 보여야 한다.
  if (progress.kind === 'retrying') key = `retrying:${progress.attempt}`;
  const text = progressText(progress);
  if (last?.key === key) return [...steps.slice(0, -1), { ...last, text }];
  return text ? [...close(steps), { key, text, past: pastText(progress), at: now }] : close(steps);
}

export type AgentAsk = (
  question: string,
  onProgress: (progress: AgentProgress) => void,
  signal: AbortSignal,
  history?: {
    question: string; answer: string; routes?: string[]; table?: TableData;
    feedback?: 'positive' | 'negative';
    result?: ResultSnapshot;
    report?: ReportDocument;
    task?: TaskSpec;
  }[],
  experiences?: ExperienceCandidate[],
  /** 사용자가 패널에서 켠 자율 모드. 저장·삭제와 전송을 이 값으로만 연다. */
  auto?: boolean,
  /** 사람이 말한 이름 → 화면이 확정한 이름. 사용자에게 붙어 대화를 넘어 산다. */
  aliases?: Record<string, string>,
  /**
   * 사람이 **이번 턴에** 올린 파일. 대화 기록에 쌓이지 않는다 — 그림 한 장이 수천
   * 토큰이라, 남겨 두면 그 뒤의 모든 턴이 같은 그림 값을 다시 낸다.
   */
  files?: UploadedFile[],
) => Promise<void>;

declare global {
  interface Window {
    __elmanAgentAsk?: AgentAsk;
    __elmanAgentPromote?: (candidate: ExperienceCandidate) => Promise<void>;
    /** 회사가 공유하는 이름 짝. 배운 것을 올리고 합쳐진 전체를 받는다. */
    __elmanAgentAliases?: (
      learned?: Record<string, string>,
      forget?: string[],
    ) => Promise<Record<string, string>>;
  }
}
